import { getAssetsPath } from "./utils/pathConfig";
import { FEATURES_CONFIG } from "./featuresConfig";
import { SFX_SPRITE_MAP } from "./sfxSpriteMap"; // AUTO-GENERATED — run "npm run build:sfx" to regenerate

// Use centralized path configuration
const BASE_PATH = getAssetsPath();

// ============================================================================
// AUDIO CONFIGURATION
// ============================================================================
export const AUDIO_CONFIG = {
  AMBIENT_MUSIC_ENABLED: true, // Enable/disable ambient music layer (plays alongside background music)
};

// ============================================================================
// AUDIO SPRITE CONFIGURATION
// ============================================================================
// SFX_SPRITE_MAP imported above from auto-generated sfxSpriteMap.js

// ============================================================================
// CONDITIONAL SOUND LOADING - Build sounds array based on enabled features
// ============================================================================
const buildSoundsArray = () => {
  const sounds = [];

  // ========================================================================
  // CORE LOOPING SOUNDS - Always loaded
  // ========================================================================
  sounds.push(
    {
      name: "background_music",
      url: `${BASE_PATH}/sounds/looping/main_background_music.mp3`,
      loop: true,
      volume: 1,
    },
    {
      name: "reel_spin",
      url: `${BASE_PATH}/sounds/looping/reel_spin.mp3`,
      volume: 1,
      loop: true,
    },
    {
      name: "counter",
      url: `${BASE_PATH}/sounds/looping/counter.mp3`,
      loop: true,
      volume: 1,
    },
    {
      name: "win_loop",
      url: `${BASE_PATH}/sounds/looping/win_loop.mp3`,
      loop: true,
      volume: 1,
    },
  );

  // ========================================================================
  // MAX WIN LOOP - Only load if max win is enabled
  // ========================================================================
  if (FEATURES_CONFIG.MAX_WIN.ENABLED) {
    sounds.push({
      name: "max_win_loop",
      url: `${BASE_PATH}/sounds/looping/max_win_loop.mp3`,
      loop: true,
      volume: 1,
    });
  }

  // ========================================================================
  // WIN LINE SOUND - Only load if win lines are enabled
  // ========================================================================
  // if (FEATURES_CONFIG.WIN_LINES.ENABLED) {
  //   sounds.push({
  //     name: "win_line",
  //     url: `${BASE_PATH}/sounds/looping/win_line.mp3`,
  //     loop: false,
  //     volume: 1,
  //   });
  // }

  // ========================================================================
  // AMBIENT MUSIC - Optional audio layer (plays alongside background music)
  // ========================================================================
  if (AUDIO_CONFIG.AMBIENT_MUSIC_ENABLED) {
    sounds.push(
      {
        name: "ambient_music",
        url: `${BASE_PATH}/sounds/looping/main_ambient_music.mp3`,
        loop: true,
        volume: 1,
      },
      {
        name: "feature_ambient_music",
        url: `${BASE_PATH}/sounds/looping/feature_ambient_music.mp3`,
        loop: true,
        volume: 1,
      },
    );
  }

  // ========================================================================
  // FREE SPIN BACKGROUND MUSIC - Only load if free spins are enabled
  // ========================================================================
  if (FEATURES_CONFIG.FREE_SPINS.ENABLED) {
    sounds.push({
      name: "free_spin_background_music",
      url: `${BASE_PATH}/sounds/looping/free_spin_background_music.mp3`,
      loop: true,
      volume: 1,
    });
  }

  // ========================================================================
  // TENSION FEATURE SOUNDS - Only load if tension is enabled
  // ========================================================================
  //   if (FEATURES_CONFIG.TENSION.ENABLED) {
  //     sounds.push({
  //       name: "tension_shake",
  //       url: `${BASE_PATH}/sounds/looping/tension_shake.mp3`,
  //       loop: true,
  //       volume: 1,
  //     });
  //   }

  // ========================================================================
  // BONUS FEATURE SOUNDS
  // ========================================================================
  sounds.push(
    {
      name: "feature_background_music",
      url: `${BASE_PATH}/sounds/looping/feature_background_music.mp3`,
      loop: true,
      volume: 1,
    },
    {
      name: "feature_reel_spin",
      url: `${BASE_PATH}/sounds/looping/feature_reel_spin.mp3`,
      volume: 1,
      loop: true,
    },

    {
      name: "total_win_loop",
      url: `${BASE_PATH}/sounds/looping/total_win_loop.mp3`,
      loop: true,
      volume: 1,
    },
    {
      name: "cm_shuffle",
      url: `${BASE_PATH}/sounds/looping/cm_shuffle.mp3`,
      loop: true,
      volume: 1,
    },
    {
      name: "main_anticipation_loop",
      url: `${BASE_PATH}/sounds/looping/main_anticipation_loop.mp3`,
      loop: true,
      volume: 1,
    },
    {
      name: "feature_anticipation_loop",
      url: `${BASE_PATH}/sounds/looping/feature_anticipation_loop.mp3`,
      loop: true,
      volume: 1,
    },
  );

  // ========================================================================
  // SFX AUDIO SPRITE - All one-shot sounds combined (48 sounds → 1 file)
  // Note: Sprite file contains all sounds, but only enabled feature sounds
  // will be accessible via SOUND_NAME export
  // ========================================================================
  sounds.push({
    name: "sfx_sprite",
    url: `${BASE_PATH}/sounds/sfx-sprite.mp3`,
    sprite: SFX_SPRITE_MAP,
    volume: 1,
  });

  return sounds;
};

export const GAME_SOUNDS = {
  sounds: buildSoundsArray(),
};

// ============================================================================
// SOUND NAME MAPPINGS
// All sound names registered here. If a sound is disabled in the sprite
// manifest or not loaded as an individual Howl, soundManager.play() silently
// does nothing — no conditional logic needed.
// ============================================================================
export const SOUND_NAME = {
  // Looping sounds (loaded individually from looping/)
  background_music: "background_music",
  ambient_music: "ambient_music",
  feature_ambient_music: "feature_ambient_music",
  reel_spin: "reel_spin",
  counter: "counter",
  win_loop: "win_loop",
  max_win_loop: "max_win_loop",
  feature_background_music: "feature_background_music",
  feature_reel_spin: "feature_reel_spin",

  total_win_loop: "total_win_loop",
  cm_shuffle: "cm_shuffle",
  feature_anticipation_loop: "feature_anticipation_loop",

  // ── One-shot sounds (loaded via sprite from one-shot/) ──

  // Core
  basic_win: "basic_win",
  bonus_end: "bonus_end",
  bonus_intro_popup: "bonus_intro_popup",
  button_click: "button_click",
  intro_popup: "intro_popup",
  spin: "spin",
  reel_stop: "reel_stop",
  quick_stop: "quick_stop",
  counter_end: "counter_end",

  // Coin / Zeus orb landing sounds
  c_in: "c_in",
  c_win: "c_win",
  cc_in: "cc_in",
  cc_update: "cc_update",
  cd_in: "cd_in",
  cd_multiply: "cd_multiply",
  cm_in: "cm_in",
  cm_stop_mini: "cm_stop_mini",
  cm_stop_minor: "cm_stop_minor",
  cm_stop_major: "cm_stop_major",
  cm_transform: "cm_transform",
  collector_in: "collector_in",
  feature_anticipation_in: "feature_anticipation_in",
  feature_anticipation_out: "feature_anticipation_out",
  gjp_reel_glow: "gjp_reel_glow",
  gjp_win: "gjp_win",
  jackpot_collect: "jackpot_collect",
  j_rotate: "j_rotate",
  j_win: "j_win",

  // Lightning bolt FX
  lightning_fly: "lightning_fly",
  lightning_end: "lightning_end",

  // Main anticipation (looping sound, played by TensionReelController)
  main_anticipation_loop: "main_anticipation_loop",

  // Particle trail FX
  particles_fly: "particles_fly",
  particles_end: "particles_end",

  // Pot sounds
  pot_in: "pot_in",
  pot_trigger: "pot_trigger",
  pot_collect: "pot_collect",
  pot_win: "pot_win",
  pot_anticipation: "pot_anticipation",
  pot_blitz_anticipation: "pot_blitz_anticipation",

  // Respin counter
  respins_counter: "respins_counter",
  respins_left_in: "respins_left_in",
  generic_popup_idle: "generic_popup_idle",

  // Win celebration (in → idle → out)
  big_win_in: "big_win_in",
  big_win_idle: "big_win_idle",
  big_win_out: "big_win_out",
  mega_win_in: "mega_win_in",
  mega_win_idle: "mega_win_idle",
  mega_win_out: "mega_win_out",
  super_mega_win_in: "super_mega_win_in",
  super_mega_win_idle: "super_mega_win_idle",
  super_mega_win_out: "super_mega_win_out",
  max_win_in: "max_win_in",
  max_win_idle: "max_win_idle",
  max_win_out: "max_win_out",
  total_win_in: "total_win_in",
  total_win_idle: "total_win_idle",
  total_win_out: "total_win_out",

  // Transition
  transition: "transition",

  // ── Framework stubs (no mp3 on disk — soundManager.play() silently no-ops) ──
  // Kept because framework code references them. Will be wired when assets arrive.
  add_respin: "add_respin",
  big_win: "big_win",
  buy_bonus: "buy_bonus",
  coin: "coin",
  coin_multiplier: "coin_multiplier",
  coin_win: "coin_win",
  collector_add: "collector_add",
  collector_appear: "collector_appear",
  collector_particle: "collector_particle",
  epic_win: "epic_win",
  expanding_wild: "expanding_wild",
  expanding_wild_multiplier: "expanding_wild_multiplier",
  feature_end: "feature_end",
  feature_reel_stop: "feature_reel_stop",
  feature_tension: "feature_tension",
  mega_win: "mega_win",
  scatter_1: "scatter_1",
  scatter_2: "scatter_2",
  scatter_win: "scatter_win",
  super_mega_win: "super_mega_win",
  super_win: "super_win",


  wild: "wild",
  win_line: "win_line",
  win_music_end: "win_music_end",
};

// ============================================================================
// PER-SOUND VOLUME MAP
// Default volume for each sound (0.0 – 1.0). soundManager.play() applies this
// automatically. Adjust individual values to balance the mix without touching
// every call site. Sounds not listed here default to 1.0.
// ============================================================================
export const SOUND_VOLUMES = {
  // ── Looping sounds ──
  background_music: 0.9,
  // Ambient layers sit 0.1 below their paired background track
  ambient_music: 0.7,
  feature_ambient_music: 0.8,
  reel_spin: 0.7,
  counter: 1.0,
  win_loop: 1.0,
  max_win_loop: 1.0,
  feature_background_music: 0.9,
  feature_reel_spin: 0.7,

  total_win_loop: 1.0,
  cm_shuffle: 1.0,

  // ── One-shot sounds ──
  basic_win: 1.0,
  bonus_end: 1.0,
  bonus_intro_popup: 1.0,
  button_click: 0.7,
  intro_popup: 1.0,
  spin: 0.7,
  reel_stop: 1.0,
  quick_stop: 1.0,
  counter_end: 1.0,
  c_in: 1.0,
  c_win: 1.0,
  cc_in: 1.0,
  cc_update: 1.0,
  cd_in: 1.0,
  cd_multiply: 1.0,
  cm_in: 1.0,
  cm_stop_mini: 1.0,
  cm_stop_minor: 1.0,
  cm_stop_major: 1.0,
  cm_transform: 1.0,
  collector_in: 1.0,
  feature_anticipation_in: 1.0,
  feature_anticipation_loop: 1.0,
  feature_anticipation_out: 1.0,
  gjp_reel_glow: 1.0,
  gjp_win: 1.0,
  jackpot_collect: 1.0,
  j_rotate: 1.0,
  j_win: 1.0,
  lightning_fly: 1.0,
  lightning_end: 1.0,
  main_anticipation_loop: 1.0,
  particles_fly: 0.7,
  particles_end: 0.7,
  pot_in: 1.0,
  pot_trigger: 1.0,
  pot_collect: 1.0,
  pot_win: 1.0,
  pot_anticipation: 1.0,
  pot_blitz_anticipation: 1.0,
  respins_counter: 0.85,
  respins_left_in: 1.0,
  generic_popup_idle: 1.0,
  big_win_in: 1.0,
  big_win_idle: 1.0,
  big_win_out: 1.0,
  mega_win_in: 1.0,
  mega_win_idle: 1.0,
  mega_win_out: 1.0,
  super_mega_win_in: 1.0,
  super_mega_win_idle: 1.0,
  super_mega_win_out: 1.0,
  max_win_in: 1.0,
  max_win_idle: 1.0,
  max_win_out: 1.0,
  total_win_in: 1.0,
  total_win_idle: 1.0,
  total_win_out: 1.0,
  transition: 1.0,
};
